import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { toast, toastError } from '../utils/toast';
import Spinner from './ui/Spinner';
import { UserButton, useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { format, isToday } from 'date-fns';
import ChatInterface from './ChatInterface';
import RunSchedule from './RunSchedule';
import NotificationSettings from './NotificationSettings';
import PromptSettings from './PromptSettings';
import Onboarding from './Onboarding';
import Paywall from './Paywall';
// MessageInput is now rendered inside ChatInterface as part of flex layout
// Removed EditResponses import - component no longer used
// StripeCheckoutMockup removed - replaced by IAP subscription flow
import VersionDisplay from './VersionDisplay';
import { ReactComponent as RuniumIcon } from '../assets/runium_icon.svg';
import { ReactComponent as ChatIcon } from '../assets/chat_icon.svg';
import { ReactComponent as CalendarIcon } from '../assets/calendar_icon.svg';
import { getApiUrl } from '../utils/apiUtils';
import FeedbackForm from './Feedback/FeedbackForm';
import { debugLog, debugWarn, debugError, debugGroupCollapsed } from '../utils/debug';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { openLegalUrl } from '../utils/legalUtils';
import { useSubscription } from '../contexts/SubscriptionContext';

const FEEDBACK_FEATURE_ENABLED = ['true', '1', 'yes', 'on'].includes(
  (process.env.REACT_APP_ENABLE_BETA_FEEDBACK || '').toLowerCase()
);
const ADMIN_ALLOWLIST = (process.env.REACT_APP_ADMIN_EMAIL_WHITELIST || '')
  .split(',')
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);
const ADMIN_EMAILS = ADMIN_ALLOWLIST.filter((value) => value.includes('@'));
const ADMIN_IDS = ADMIN_ALLOWLIST.filter((value) => !value.includes('@'));

// Loading skeleton component following Tailwind config
const UserButtonSkeleton = () => (
  <div className="w-8 h-8 bg-gray-750 rounded-full animate-pulse" />
);

// Removed unused MultiSessionIcon component

const Dashboard = () => {
  // Mobile menu state - currently unused but kept for future mobile menu feature
  // eslint-disable-next-line no-unused-vars
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'schedule', or 'notification-settings'
  const [userOnboardingStatus, setUserOnboardingStatus] = useState(null);
  // Initialize to null (unknown) — effectiveShowLanding useMemo will resolve
  // this synchronously using localStorage before the first paint, so returning
  // users never see the Onboarding component flash.
  const [showLanding, setShowLanding] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false); // Show paywall after onboarding
  // Removed showEditResponses - users now go directly to onboarding considerations screen
  // Stripe checkout removed - replaced by IAP subscription flow
  const [isDeleting, setIsDeleting] = useState(false); // Loading state for delete account
  const [loadingTimeout, setLoadingTimeout] = useState(false); // Track if we've timed out
  const [networkError, setNetworkError] = useState(false); // Track if there are network connectivity issues
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { loaded: clerkLoaded, signOut } = useClerk();
  const navigate = useNavigate();
  const { isSubscribed, isLoading: subscriptionLoading, purchase, redeemPromoCode, restorePurchases, checkSubscription } = useSubscription();
  
  // Swipe gesture state for main view navigation
  const [mainTouchStart, setMainTouchStart] = useState(null);
  const [mainSwipeOffset, setMainSwipeOffset] = useState(0);
  const [isMainSwiping, setIsMainSwiping] = useState(false);
  const [isMainAnimating, setIsMainAnimating] = useState(false);
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  // Removed unused showChatInterface and newMessage states (MessageInput now in ChatInterface)
  // cursorPosition removed - MessageInput now handles its own state in ChatInterface
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackRecentActions, setFeedbackRecentActions] = useState([]);
  const [eventBasedFeedbackDefaults, setEventBasedFeedbackDefaults] = useState(null);
  // 🎯 FIX: Track in-flight onboarding check to prevent multiple concurrent requests
  const checkingOnboardingRef = useRef(false);
  const onboardingCheckControllerRef = useRef(null);
  const isAdminUser = useMemo(() => {
    const email = user?.primaryEmailAddress?.emailAddress?.toLowerCase();
    const clerkId = user?.id?.toLowerCase();
    if (!ADMIN_ALLOWLIST.length) {
      return false;
    }
    return (email && ADMIN_EMAILS.includes(email)) || (clerkId && ADMIN_IDS.includes(clerkId));
  }, [user]);
  const mainContainerRef = useRef(null);
  const mainAnimationRef = useRef(null);
  const chatInterfaceRef = useRef(null);
  const schedulePollingIntervalRef = useRef(null);

  const feedbackDefaults = useMemo(() => {
    // Event-based defaults take precedence
    if (eventBasedFeedbackDefaults) {
      return eventBasedFeedbackDefaults;
    }

    if (networkError) {
      return {
        type: 'bug',
        title: 'Connection issue while using Coach',
        context: 'network',
      };
    }

    if (activeTab === 'schedule') {
      return {
        type: 'feedback',
        title: 'Feedback about the schedule view',
        context: 'schedule',
      };
    }

    if (activeTab === 'notification-settings') {
      return {
        type: 'feedback',
        title: 'Notification preferences feedback',
        context: 'notification-settings',
      };
    }

    if (activeTab === 'prompt-settings') {
      return {
        type: 'feature_request',
        title: 'Coaching prompt improvements',
        context: 'prompt-settings',
      };
    }

    return {
      type: 'feedback',
      title: 'Feedback about Runium',
      context: activeTab || 'chat',
    };
  }, [activeTab, networkError, eventBasedFeedbackDefaults]);

  const recordFeedbackAction = useCallback((label) => {
    setFeedbackRecentActions((previous) => {
      const next = [...previous.slice(-9), label];
      return next;
    });
  }, []);

  useEffect(() => {
    if (activeTab) {
      recordFeedbackAction(`view:${activeTab}`);
    }
  }, [activeTab, recordFeedbackAction]);

  useEffect(() => {
    recordFeedbackAction(networkError ? 'network:error' : 'network:ok');
  }, [networkError, recordFeedbackAction]);

  useEffect(() => {
    if (showFeedbackForm) {
      recordFeedbackAction('ui:open_feedback_form');
    }
  }, [showFeedbackForm, recordFeedbackAction]);

  // Listen for custom event to open feedback form (e.g., from message "Report Issue" button)
  useEffect(() => {
    const handleOpenFeedback = (e) => {
      const { defaultType, defaultTitle, contextSource } = e.detail || {};
      if (defaultType || defaultTitle || contextSource) {
        setEventBasedFeedbackDefaults({
          type: defaultType || 'bug',
          title: defaultTitle || 'Feedback about Runium',
          context: contextSource || 'chat',
        });
      }
      setShowFeedbackForm(true);
    };

    window.addEventListener('open-feedback-form', handleOpenFeedback);
    return () => window.removeEventListener('open-feedback-form', handleOpenFeedback);
  }, []);

  // Clear event-based defaults when feedback form closes
  useEffect(() => {
    if (!showFeedbackForm) {
      setEventBasedFeedbackDefaults(null);
    }
  }, [showFeedbackForm]);

  // Check for feedback parameter in URL to open feedback form
  useEffect(() => {
    if (typeof window === 'undefined' || !FEEDBACK_FEATURE_ENABLED) {
      return;
    }

    // Check URL search params (e.g., ?feedback or ?feedback=true)
    const searchParams = new URLSearchParams(window.location.search);
    const feedbackParam = searchParams.get('feedback');
    
    // Check hash fragment (e.g., #feedback)
    const hash = window.location.hash.slice(1); // Remove the # symbol
    
    if (feedbackParam === '' || feedbackParam === 'true' || hash === 'feedback') {
      setShowFeedbackForm(true);
      
      // Clean up the URL after opening the form
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('feedback');
      if (hash === 'feedback') {
        newUrl.hash = '';
      }
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []); // Only run once on mount

  // Handle Stripe checkout return (redirect back from Stripe)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subParam = params.get('subscription');
    if (subParam === 'success') {
      debugLog('[Subscription] Stripe checkout completed successfully');
      // Re-check subscription status from backend
      checkSubscription();
      // Clean URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
    } else if (subParam === 'cancelled') {
      debugLog('[Subscription] Stripe checkout cancelled');
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []); // Only run once on mount

  const experimentAssignments = useMemo(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      const raw = window.localStorage.getItem('runium_experiments');
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      debugWarn('Unable to parse experiment assignments', error);
      return {};
    }
  }, []);

  // PDF generation function - simplified approach
  const handleGeneratePDF = async () => {
    try {
      // Dispatch the event to CalendarGrid to handle PDF generation
      // This uses the original working implementation
      window.dispatchEvent(new Event('runium:generate-week-pdf'));
    } catch (error) {
      debugError('Error generating PDF:', error);
      toastError('Could not generate PDF');
    }
  };

  // Compute effective onboarding state synchronously so the very first paint
  // after Clerk loads never shows the Onboarding component for returning users.
  // showLanding is initialised to null (unknown). This memo resolves it to
  // true/false using localStorage + API status without waiting for a useEffect.
  const effectiveShowLanding = useMemo(() => {
    // If showLanding has been explicitly set by an effect, respect it —
    // but still allow API override.
    if (showLanding === false) return false;

    if (user && isSignedIn) {
      const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
      const hasConversations = localStorage.getItem(`has_conversations_${user.id}`);
      const hasUsedApp = localStorage.getItem(`user_${user.id}_has_used_app`);
      const hasAppEvidence = hasCompletedOnboarding || hasConversations || hasUsedApp;

      const apiHasResponded = userOnboardingStatus !== null;
      const apiSaysNotComplete = apiHasResponded && userOnboardingStatus === false;

      if (apiSaysNotComplete) {
        // API explicitly says onboarding not complete — trust the API
        debugLog('🔍 [ONBOARDING] API says not complete - showing onboarding');
        return true;
      }

      if (hasAppEvidence) {
        // Returning user — skip onboarding immediately (no flash)
        return false;
      }

      // No localStorage evidence and API hasn't responded yet — stay in
      // unknown/loading state so we don't flash Onboarding for users whose
      // data simply hasn't loaded yet.
      if (!apiHasResponded) return null;

      // API responded and didn't say "not complete" + no local evidence →
      // genuinely new user, show onboarding.
      return true;
    }

    // user not available yet (Clerk still loading) — stay unknown
    return showLanding;
  }, [user, isSignedIn, showLanding, userOnboardingStatus]);

  // Apply chat-mode class only when showing the actual chat interface.
  // useLayoutEffect runs synchronously before the browser paints, preventing
  // the visible layout shift (flash) when switching from non-fixed to fixed positioning.
  useLayoutEffect(() => {
    // Only add chat-mode when we're showing the dashboard (not landing, not paywall)
    if (effectiveShowLanding === false && !showPaywall) {
      document.body.classList.add('chat-mode');
    } else {
      document.body.classList.remove('chat-mode');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('chat-mode');
    };
  }, [effectiveShowLanding, showPaywall]);

  // Add a failsafe timeout to prevent infinite loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showLanding === null) {
        debugError('Dashboard: Loading timeout - checking user state and deciding fallback');
        setLoadingTimeout(true);
        
        // If user is signed in, try to show dashboard based on localStorage
        if (user && isSignedIn) {
          const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
          const hasSeenPaywall = localStorage.getItem(`paywall_seen_${user.id}`);
          
          if (hasCompletedOnboarding) {
            debugLog('Dashboard: Timeout fallback - localStorage shows onboarding completed, showing dashboard');
            setUserOnboardingStatus(true);
            setShowLanding(false);
          } else {
            debugLog('Dashboard: Timeout fallback - no onboarding evidence, showing onboarding');
            setShowLanding(true);
          }
        } else {
          debugLog('Dashboard: Timeout fallback - no user, showing landing');
          setShowLanding(true);
        }
      }
    }, 15000); // 15 second total timeout

    return () => clearTimeout(timeoutId);
  }, [showLanding, user, isSignedIn]);

  // Removed unused fetchWithRetry function
  // eslint-disable-next-line no-unused-vars
  const fetchWithRetry = async (url, options = {}, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        return response; // Return on first success
      } catch (error) {
        lastError = error;
        
        // Don't retry on the last attempt
        if (attempt < maxRetries - 1) {
          // Calculate exponential backoff delay: 1s, 2s, 4s
          const delay = 1000 * Math.pow(2, attempt);
          debugLog(`🔄 [DASHBOARD RETRY] Attempt ${attempt + 1}/${maxRetries} failed for ${url}, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed, throw the last error
    throw lastError;
  };

  // Check user onboarding status
  useEffect(() => {
    // Only run when Clerk has fully loaded
    if (!clerkLoaded || !userLoaded) {
      debugLog('Dashboard: Waiting for Clerk to load...');
      return;
    }
    
    // 🎯 FIX: Immediate fallback to localStorage for instant rendering
    // This allows calendar to show immediately while API check happens in background
    // Only run this once per user session to prevent multiple state updates
    if (user && isSignedIn) {
      const fallbackKey = `dashboard_fallback_${user.id}`;
      if (!sessionStorage.getItem(fallbackKey)) {
        sessionStorage.setItem(fallbackKey, 'true');
        
        const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
        const hasSeenPaywall = localStorage.getItem(`paywall_seen_${user.id}`);
        const hasConversations = localStorage.getItem(`has_conversations_${user.id}`);
        const hasUsedApp = localStorage.getItem(`user_${user.id}_has_used_app`);
        
        // Check for any evidence that user has used the app before
        const hasAppEvidence = hasCompletedOnboarding || hasConversations || hasUsedApp;
        
        if (hasAppEvidence) {
          debugGroupCollapsed('📊 [DASHBOARD] localStorage fallback', () => {
            debugLog('Dashboard: Using localStorage fallback for instant rendering - showing dashboard immediately');
          });
          setUserOnboardingStatus(true);
          if (!hasSeenPaywall && !hasCompletedOnboarding) {
            // Will show paywall, but allow calendar to render first
            setShowPaywall(false);
            setShowLanding(false);
          } else {
            setShowLanding(false);
          }
          // Continue with API check in background, but don't block rendering
        }
        // If no app evidence, showLanding stays true (onboarding) - no state update needed
      }
    }
    
    // 🎯 FIX: Prevent multiple concurrent requests (handles React StrictMode double-invoke)
    // Use a more robust check that includes a timestamp to handle rapid re-renders
    const checkKey = `onboarding_check_${user?.id || 'no-user'}`;
    const now = Date.now();
    
    // Check ref first (fastest check, no storage access)
    if (checkingOnboardingRef.current) {
      debugLog('Dashboard: Onboarding check already in progress, skipping duplicate request');
      return;
    }
    
    // Check sessionStorage for recent check (500ms window)
    const lastCheckTime = sessionStorage.getItem(checkKey);
    if (lastCheckTime && (now - parseInt(lastCheckTime, 10)) < 500) {
      debugLog('Dashboard: Recent check detected, skipping duplicate request');
      return;
    }
    
    // Set both ref and sessionStorage atomically before making request
    checkingOnboardingRef.current = true;
    sessionStorage.setItem(checkKey, now.toString());
    setTimeout(() => sessionStorage.removeItem(checkKey), 1000);
    
    // 🎯 FIX: Cancel any previous in-flight request
    if (onboardingCheckControllerRef.current) {
      onboardingCheckControllerRef.current.abort();
      onboardingCheckControllerRef.current = null;
    }
    
    const checkOnboardingStatus = async () => {
      // Checking onboarding status...
      
      // If user is explicitly not signed in, show landing immediately
      if (isSignedIn === false) {
        debugLog('Dashboard: User is not signed in, showing landing');
        setShowLanding(true);
        checkingOnboardingRef.current = false;
        return;
      }
      
      if (user && isSignedIn) {
        try {
          const API_URL = getApiUrl();
          const token = await getToken();
          
          if (!token) {
            setShowLanding(true);
            checkingOnboardingRef.current = false;
            return;
          }
          
          // 🎯 FIX: Use 5 second timeout for onboarding status check (was 3s, too short)
          const controller = new AbortController();
          onboardingCheckControllerRef.current = controller;
          const startTime = Date.now();
          const timeoutId = setTimeout(() => {
            const elapsed = Date.now() - startTime;
            debugLog(`Dashboard: Request timed out after ${elapsed}ms`);
            controller.abort();
          }, 5000); // 5 seconds timeout
          
          // Direct fetch without retry to avoid connection pool exhaustion
          const response = await fetch(`${API_URL}/user/onboarding-status`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal,
          });
          
          const elapsed = Date.now() - startTime;
          // Prevent duplicate logs from React StrictMode (only log once per 500ms)
          const logKey = `dashboard_status_${elapsed}`;
          if (!sessionStorage.getItem(logKey)) {
            sessionStorage.setItem(logKey, 'true');
            setTimeout(() => sessionStorage.removeItem(logKey), 500);
            debugLog(`📊 [DASHBOARD] /user/onboarding-status: ${elapsed}ms (status: ${response.status})`);
          }
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            debugError('Dashboard: Failed to get onboarding status, status:', response.status);
            // Backend is up but can't find user - show system unavailable instead of onboarding
            // This handles the case where backend restarted and lost in-memory state
            setNetworkError(true);
            return;
          }
          
          const data = await response.json();
          
          // Only update state if it actually changed to prevent unnecessary re-renders
          setUserOnboardingStatus(prev => {
            if (prev === data.onboard_complete) return prev;
            return data.onboard_complete;
          });
          
          if (!data.onboard_complete) {
            // 🎯 FIX: API says user hasn't completed onboarding - clear any stale localStorage
            // This fixes the issue where stale localStorage overrides the correct API response
            const staleOnboardingKey = `onboarding_completed_${user.id}`;
            const staleOnboarding = localStorage.getItem(staleOnboardingKey);
            if (staleOnboarding === 'true') {
              debugLog('🔍 [ONBOARDING] Clearing stale localStorage - API says not complete but localStorage says complete');
              localStorage.removeItem(staleOnboardingKey);
              // Also clear other potentially stale keys
              localStorage.removeItem(`has_conversations_${user.id}`);
              localStorage.removeItem(`user_${user.id}_has_used_app`);
            }
            
            // Show onboarding if not complete - only update if value changed
            setShowLanding(prev => {
              if (prev === true) return prev; // Already true, no update needed
              return true;
            });
          } else {
            // Onboarding complete — paywall will be shown by the useEffect
            // if subscription is not active (no localStorage gate needed)
            setShowLanding(prev => {
              if (prev === false) return prev;
              return false;
            });
          }
          checkingOnboardingRef.current = false;
          onboardingCheckControllerRef.current = null;
        } catch (error) {
          // Only set networkError for actual connectivity issues, not API errors
          if (error.name === 'AbortError') {
            debugLog('Dashboard: Request timed out, using fallback');
            setNetworkError(true);
          } else if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('ERR_CONNECTION'))) {
            debugError('Dashboard: Network connectivity issue - cannot reach backend server');
            setNetworkError(true);
          } else {
            debugError('Dashboard: Error checking onboarding status:', error);
            // Don't set networkError for other errors - backend may be up but returning errors
          }
          
          // 🎯 FIX: Better fallback logic - check localStorage for user state
          const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
      const hasConversations = localStorage.getItem(`has_conversations_${user.id}`);
      const hasUsedApp = localStorage.getItem(`user_${user.id}_has_used_app`);

      // Check for any evidence that user has used the app before
      const hasAppEvidence = hasCompletedOnboarding || hasConversations || hasUsedApp;

          if (hasAppEvidence) {
            debugLog('Dashboard: Onboarding status check failed, but localStorage shows user has used app - showing dashboard');
            setUserOnboardingStatus(true);
            setShowLanding(false);
          } else {
            debugLog('Dashboard: Onboarding status check failed and no app evidence - showing system unavailable');
            // Don't show onboarding to signed-in users when backend fails - show system unavailable instead
            setNetworkError(true);
          }
          checkingOnboardingRef.current = false;
          onboardingCheckControllerRef.current = null;
        }
      } else {
        debugLog('Dashboard: No user logged in or not signed in, showing landing');
        debugLog('Dashboard: userLoaded:', userLoaded, 'isSignedIn:', isSignedIn, 'user:', !!user);
        // No user logged in, show landing
        setShowLanding(true);
        checkingOnboardingRef.current = false;
      }
    };

    checkOnboardingStatus();
    
    // 🎯 FIX: Cleanup function to cancel request on unmount/dependency change
    return () => {
      if (onboardingCheckControllerRef.current) {
        onboardingCheckControllerRef.current.abort();
        onboardingCheckControllerRef.current = null;
      }
      checkingOnboardingRef.current = false;
    };
    
    // Removed unused showChatInterface logic - ChatInterface now handles its own mounting
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, clerkLoaded, userLoaded, isSignedIn]);

  // Send legal acknowledgments to backend after sign-up
  useEffect(() => {
    if (FEATURE_FLAGS.AGE_VERIFICATION && user && isSignedIn && userLoaded) {
      const stored = localStorage.getItem('legal_acknowledgments');
      if (stored) {
        try {
          const acknowledgments = JSON.parse(stored);
          const API_URL = getApiUrl();
          getToken().then(token => {
            fetch(`${API_URL}/user/legal-acknowledgments`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                age_verified: acknowledgments.age_verified,
                medical_disclaimer_accepted: acknowledgments.medical_disclaimer_accepted
              })
            }).then(() => {
              // Clear from localStorage after successful send
              localStorage.removeItem('legal_acknowledgments');
            }).catch(err => {
              debugError('Failed to send legal acknowledgments:', err);
            });
          });
        } catch (err) {
          debugError('Failed to parse legal acknowledgments:', err);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSignedIn, userLoaded]);

  // Listen for schedule completion to stop polling early and log completion
  useEffect(() => {
    const handleScheduleReady = () => {
      // Log completion with timing if available
      if (user) {
        const scheduleStartTime = localStorage.getItem(`schedule_start_time_${user.id}`);
        if (scheduleStartTime) {
          const startTime = parseInt(scheduleStartTime, 10);
          const totalTime = Date.now() - startTime;
          const totalSeconds = (totalTime / 1000).toFixed(1);
          debugLog(`✅ [SCHEDULE GENERATION COMPLETE] Total time: ${totalTime}ms / ${totalSeconds}s`);
          localStorage.removeItem(`schedule_start_time_${user.id}`);
        } else {
          debugLog('✅ [SCHEDULE GENERATION COMPLETE] (timing not available)');
        }
      }
      
      if (schedulePollingIntervalRef.current) {
        debugLog('Schedule is ready, stopping polling early');
        clearInterval(schedulePollingIntervalRef.current);
        schedulePollingIntervalRef.current = null;
      }
    };

    window.addEventListener('runium:schedule-ready', handleScheduleReady);
    
    return () => {
      window.removeEventListener('runium:schedule-ready', handleScheduleReady);
      // Clean up polling interval on unmount
      if (schedulePollingIntervalRef.current) {
        clearInterval(schedulePollingIntervalRef.current);
        schedulePollingIntervalRef.current = null;
      }
    };
  }, [user]);

  // Handle navigation to schedule from chat cards
  const handleNavigateToSchedule = (scheduleDetails) => {
    setActiveTab('schedule');
    // Trigger schedule refresh to ensure latest data is loaded
    window.dispatchEvent(new Event('runium:refresh-schedule'));
    // You could potentially scroll to or highlight specific schedule here
    if (scheduleDetails) {
      debugLog('Navigating to schedule:', scheduleDetails);
    } else {
      debugLog('Navigating to schedule (no details provided)');
    }
  };

  // Removed unused handleSendMessage, MAIN_SWIPE_THRESHOLD, MAIN_SWIPE_VELOCITY_THRESHOLD
  
  // Smooth animation for main view transitions
  const animateMainViewTo = useCallback((targetTab) => {
    // Don't animate if we're on settings pages
    if (activeTab === 'notification-settings' || activeTab === 'prompt-settings') {
      setActiveTab(targetTab);
      return;
    }
    
    setIsMainAnimating(true);
    setIsMainSwiping(false);
    
    // Clear any existing animation timeout
    if (mainAnimationRef.current) {
      clearTimeout(mainAnimationRef.current);
    }
    
    // Set the final state
    setActiveTab(targetTab);
    
    // Reset swipe offset after animation
    mainAnimationRef.current = setTimeout(() => {
      setMainSwipeOffset(0);
      setIsMainAnimating(false);
    }, 300);
  }, [activeTab]);
  
  // Add keyboard navigation between chat and schedule
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't interfere with input fields or settings pages
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || 
          activeTab === 'notification-settings' || activeTab === 'prompt-settings') return;
      
      // Arrow key navigation
      if (e.key === 'ArrowLeft' && activeTab === 'schedule') {
        animateMainViewTo('chat');
      } else if (e.key === 'ArrowRight' && activeTab === 'chat') {
        animateMainViewTo('schedule');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, animateMainViewTo]);
  
  // Main view touch handlers for horizontal swiping between chat and schedule
  const handleMainTouchStart = useCallback((e) => {
    // Don't interfere with settings pages, when animating, or when sidebar is open
    if (activeTab === 'notification-settings' || activeTab === 'prompt-settings' || isMainAnimating || isChatSidebarOpen) return;
    
    // Don't interfere if touch is on interactive elements or calendar rows
    const target = e.target;
    if (target.closest('button') || target.closest('input') || target.closest('textarea') || target.closest('.sidebar') || target.closest('[data-clickable]') || target.closest('.calendar-row')) return;
    
    // CRITICAL: Exclude touches in the bottom input area (where MessageInput lives)
    const touch = e.touches[0];
    const touchY = touch.clientY;
    const windowHeight = window.innerHeight;
    
    // More robust exclusion: check if touch is in the bottom 200px OR if target is textarea
    if (touchY > windowHeight - 200 || target.closest('textarea')) {
      // Touch is in the bottom area where MessageInput is or directly on textarea
      return;
    }
    
    setMainTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setMainSwipeOffset(0);
    setIsMainSwiping(false);
  }, [activeTab, isMainAnimating, isChatSidebarOpen]);
  
  const handleMainTouchMove = useCallback((e) => {
    if (!mainTouchStart || isMainAnimating || isChatSidebarOpen) return;
    
    // CRITICAL: Also check here if touch is in input area
    const touch = e.touches[0];
    const touchY = touch.clientY;
    const windowHeight = window.innerHeight;
    
    // More robust exclusion: check if touch is in the bottom 200px OR if target is textarea
    const target = e.target;
    if (touchY > windowHeight - 200 || target.closest('textarea')) {
      // Touch is in the bottom area where MessageInput is or directly on textarea - don't interfere
      return;
    }
    
    const deltaX = touch.clientX - mainTouchStart.x;
    const deltaY = touch.clientY - mainTouchStart.y;
    const viewportWidth = window.innerWidth;
    
    // Only start swiping if horizontal movement is dominant
    if (!isMainSwiping) {
      if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        // Check if we can swipe in this direction
        if ((activeTab === 'chat' && deltaX < 0) || (activeTab === 'schedule' && deltaX > 0)) {
          setIsMainSwiping(true);
        }
      }
    }
    
    if (isMainSwiping) {
      // Prevent vertical scrolling while swiping
      e.preventDefault();
      
      // Convert to percentage offset for proper scaling with a cap to prevent acceleration
      let percentOffset = (deltaX / viewportWidth) * 100;
      
      // Cap the offset to prevent excessive acceleration
      percentOffset = Math.max(-50, Math.min(50, percentOffset));
      
      // Apply resistance at edges
      if ((activeTab === 'chat' && deltaX > 0) || (activeTab === 'schedule' && deltaX < 0)) {
        // Can't swipe beyond edges
        percentOffset = percentOffset * 0.2; // Heavy resistance
      }
      
      setMainSwipeOffset(percentOffset);
    }
  }, [mainTouchStart, isMainSwiping, activeTab, isMainAnimating, isChatSidebarOpen]);
  
  const handleMainTouchEnd = useCallback((e) => {
    if (!mainTouchStart || !isMainSwiping || isMainAnimating) return;
    
    // Also check if touch ended in input area
    if (e && e.changedTouches && e.changedTouches[0]) {
      const touch = e.changedTouches[0];
      const touchY = touch.clientY;
      const windowHeight = window.innerHeight;
      
      // More robust exclusion: check if touch is in the bottom 200px OR if target is textarea
      const target = e.target;
      if (touchY > windowHeight - 200 || target.closest('textarea')) {
        return;
      }
    }
    
    const deltaTime = Date.now() - mainTouchStart.time;
    const velocity = Math.abs(mainSwipeOffset) / deltaTime;
    
    // Determine if we should change tabs (threshold is now in percentage)
    const PERCENT_THRESHOLD = 15; // 15% of viewport width
    let shouldSwitch = false;
    let targetTab = activeTab;
    
    if (activeTab === 'chat' && mainSwipeOffset < 0) {
      // Swiping left from chat -> go to schedule
      shouldSwitch = Math.abs(mainSwipeOffset) > PERCENT_THRESHOLD || velocity > 0.1;
      if (shouldSwitch) targetTab = 'schedule';
    } else if (activeTab === 'schedule' && mainSwipeOffset > 0) {
      // Swiping right from schedule -> go to chat
      shouldSwitch = Math.abs(mainSwipeOffset) > PERCENT_THRESHOLD || velocity > 0.1;
      if (shouldSwitch) targetTab = 'chat';
    }
    
    // Animate to final position
    animateMainViewTo(targetTab);
    
    // Reset touch start
    setMainTouchStart(null);
  }, [mainTouchStart, isMainSwiping, mainSwipeOffset, activeTab, isMainAnimating, animateMainViewTo]);
  
  // Removed unused getMainContentTransform function


  // Handle delete account - delete all data AND Clerk account
  const handleDeleteAccount = async () => {
    if (!user || isDeleting) return;
    
    // Strong confirmation dialog with detailed information
    const confirmed = window.confirm(
      '⚠️ PERMANENT ACCOUNT DELETION\n\n' +
      'This will permanently delete your account and ALL associated data:\n' +
      '• All conversations and messages\n' +
      '• All running schedules and workouts\n' +
      '• All preferences and settings\n' +
      '• Your authentication account\n\n' +
      'This action CANNOT be undone.\n\n' +
      'Are you absolutely sure you want to delete your account?'
    );
    
    if (!confirmed) {
      return;
    }
    
    // Double confirmation
    if (!window.confirm('⚠️ Final confirmation: This will delete your account permanently. Click OK to proceed or Cancel to abort.')) {
      return;
    }
    
    setIsDeleting(true);
    
    try {
      const API_URL = getApiUrl();
      debugLog('🗑️ Delete account: Getting auth token...');
      const token = await getToken();
      debugLog('✅ Delete account: Token retrieved');
      
      // Add timeout to prevent hanging (60 seconds to allow for Clerk deletion retries + database deletion)
      // Clerk deletion with retries: ~25 seconds (3 attempts with 2s, 4s, 8s backoff + API calls)
      // Database deletion: ~5-10 seconds
      // Total: ~30-35 seconds, so 60 seconds provides a safe buffer
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        debugLog('⏰ Delete account: Request timeout after 60 seconds');
        controller.abort();
      }, 60000); // 60 second timeout
      
      let response;
      try {
        response = await fetch(`${API_URL}/user/delete-account`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        // Re-throw to be caught by outer catch block
        throw fetchError;
      }
      
      debugLog('✅ Delete account: Response received, status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(`HTTP ${response.status}: ${errorData.detail || response.statusText}`);
      }
      
      const data = await response.json();
      debugLog('✅ Delete account: Response data:', data);
      
      if (data.success) {
        debugLog('✅ Account deleted successfully');

        // Show warning if Clerk deletion failed but database was deleted
        if (data.warning) {
          debugWarn('⚠️ Clerk deletion warning:', data.warning);
        }

        // Clear all user-specific localStorage keys so re-signup starts fresh
        if (user?.id) {
          const uid = user.id;
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.includes(uid)) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          debugLog(`✅ Cleared ${keysToRemove.length} localStorage keys for user ${uid}`);
        }

        // Sign out and redirect to sign-in
        try {
          await signOut();
        } catch (error) {
          debugError('signOut failed (account already deleted):', error);
        }
        // Always redirect, even if signOut fails
        window.location.href = '/sign-in';
      } else {
        toastError(`Error: ${data.message || 'Failed to delete account'}`);
        setIsDeleting(false);
      }
    } catch (error) {
      debugError('Error during account deletion:', error);
      
      // Handle timeout and network errors
      let errorMessage = error.message;
      if (error.name === 'AbortError' || error.message.includes('aborted')) {
        errorMessage = 'Request timed out after 60 seconds. The deletion may still be in progress on the server. Please wait a moment and try signing in again - if your account is deleted, you will be redirected to sign-in.';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      toastError(`Failed to delete account: ${errorMessage}`);
      setIsDeleting(false);
    }
  };

  const renderActiveTab = () => {
    // For settings pages, render normally
    if (activeTab === 'notification-settings' || activeTab === 'prompt-settings') {
      return (
        <>
          {activeTab === 'notification-settings' && <NotificationSettings onNavigateBack={() => setActiveTab('chat')} />}
          {activeTab === 'prompt-settings' && <PromptSettings onNavigateBack={() => setActiveTab('chat')} activeConversationId={activeConversationId} />}
        </>
      );
    }
    
    // For chat and schedule, use horizontal sliding layout
    return (
      <div
        className="relative w-full h-full overflow-hidden"
        style={{ touchAction: 'pan-y pinch-zoom' }}
        onTouchStart={handleMainTouchStart}
        onTouchMove={handleMainTouchMove}
        onTouchEnd={handleMainTouchEnd}
        ref={mainContainerRef}
      >
        <div 
          className="flex h-full"
          style={{
            width: '200%',
            transform: (() => {
              if (isMainSwiping) {
                // While swiping, add offset to the current position
                const baseOffset = activeTab === 'schedule' ? -50 : 0;
                return `translateX(${baseOffset + mainSwipeOffset}%)`;
              } else {
                // Not swiping, show the active tab
                return activeTab === 'schedule' ? 'translateX(-50%)' : 'translateX(0)';
              }
            })(),
            transition: isMainAnimating || (!isMainSwiping && mainSwipeOffset !== 0) ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          {/* Chat Interface - always rendered */}
          <div className="w-1/2 h-full flex-shrink-0 overflow-hidden">
            <ChatInterface 
              ref={chatInterfaceRef}
              onNavigateToSchedule={handleNavigateToSchedule}
              onSidebarStateChange={setIsChatSidebarOpen}
              onActiveConversationChange={setActiveConversationId}
              activeTab={activeTab}
            />
          </div>
          
          {/* Schedule - always rendered */}
          <div className="w-1/2 h-full flex-shrink-0">
            <RunSchedule />
          </div>
        </div>
        
        {/* Visual indicators for swipe - with fade out */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Left edge indicator when swiping right from schedule */}
          <div 
            className="absolute left-0 top-0 h-full w-2 bg-gradient-to-r from-blue-500/30 to-transparent transition-opacity duration-200"
            style={{
              opacity: activeTab === 'schedule' && mainSwipeOffset > 0 && isMainSwiping
                ? Math.min(mainSwipeOffset / 10, 1)
                : 0
            }}
          />
          
          {/* Right edge indicator when swiping left from chat */}
          <div 
            className="absolute right-0 top-0 h-full w-2 bg-gradient-to-l from-blue-500/30 to-transparent transition-opacity duration-200"
            style={{
              opacity: activeTab === 'chat' && mainSwipeOffset < 0 && isMainSwiping
                ? Math.min(Math.abs(mainSwipeOffset) / 10, 1)
                : 0
            }}
          />
        </div>
        
        {/* Page indicator dots at bottom - only on mobile, fade when not swiping */}
        <div 
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 md:hidden transition-opacity duration-200"
          style={{
            opacity: isMainSwiping ? 1 : 0,
            pointerEvents: 'none'
          }}
        >
          <div 
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeTab === 'chat' || (isMainSwiping && mainSwipeOffset > 0 && activeTab === 'schedule') 
                ? 'bg-blue-500 w-6' : 'bg-gray-600'
            }`}
          />
          <div 
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeTab === 'schedule' || (isMainSwiping && mainSwipeOffset < 0 && activeTab === 'chat')
                ? 'bg-blue-500 w-6' : 'bg-gray-600'
            }`}
          />
        </div>
      </div>
    );
  };

  // If paywall is showing but user became subscribed (e.g. restored), auto-dismiss
  useEffect(() => {
    if (showPaywall && isSubscribed && !subscriptionLoading) {
      setShowPaywall(false);
      setActiveTab('chat');
    }
  }, [showPaywall, isSubscribed, subscriptionLoading]);

  // Re-show paywall if subscription expires while on dashboard
  useEffect(() => {
    if (FEATURE_FLAGS.PAID_SUBSCRIPTIONS && !isSubscribed && !subscriptionLoading && effectiveShowLanding === false && !showPaywall) {
      const hasOnboarded = user && localStorage.getItem(`onboarding_completed_${user.id}`);
      if (hasOnboarded) {
        debugLog('[Subscription] Expired — re-showing paywall');
        setShowPaywall(true);
      }
    }
  }, [isSubscribed, subscriptionLoading, effectiveShowLanding, showPaywall, user]);

  // 🎯 FIX: Only block on Clerk loading, not onboarding check
  // Allow calendar to render even if onboarding status is still being checked
  if (!clerkLoaded || !userLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="md" tone="blue" className="mx-auto mb-4" />
          <p className="text-gray-300">Loading...</p>
          {loadingTimeout && (
            <p className="mt-3 text-xs text-yellow-400">Taking longer than usual…</p>
          )}
          {networkError && (
            <p className="mt-3 text-xs text-yellow-400">Connection issue — retrying</p>
          )}
        </div>
      </div>
    );
  }

  // effectiveShowLanding is now computed via useMemo near the top of the component

  // Function to check for existing schedule and start enhancement (called after paywall)
  const checkAndStartEnhancement = async () => {
    debugLog('🎯 [ENHANCEMENT] Starting checkAndStartEnhancement function');
    
    // 🎯 FIX: Check if we just completed onboarding - if so, schedule generation is already in progress
    const onboardingConvId = localStorage.getItem(`onboarding_conversation_id_${user?.id}`);
    if (onboardingConvId) {
      debugLog('🎯 [ENHANCEMENT] Onboarding just completed, schedule generation already in progress, skipping Dashboard generation');
      return;
    }
    
    try {
      const API_URL = getApiUrl();
      const token = await getToken();
      debugLog('🎯 [ENHANCEMENT] Got API token, checking for existing schedules...');
      
      // Check if schedules already exist
      const existingSchedulesResponse = await fetch(`${API_URL}/api/regimens/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      debugLog('🎯 [ENHANCEMENT] Existing schedules API response status:', existingSchedulesResponse.status);
      
      if (existingSchedulesResponse.ok) {
        const existingSchedules = await existingSchedulesResponse.json();
        debugLog('🎯 [ENHANCEMENT] Found', existingSchedules?.length || 0, 'existing schedules');
        
        if (existingSchedules && existingSchedules.length > 0) {
          debugLog('✅ [ENHANCEMENT] Basic schedules found, starting enhancement process');
          debugLog('🎯 [ENHANCEMENT] Schedule details:', existingSchedules.map(s => ({
            id: s.regimen_id,
            week: s.start_of_week_date,
            created: s.created_at,
            updated: s.updated_at
          })));
          
          // Set start time for tracking enhancement duration
          const scheduleStartTime = Date.now();
          if (user) {
            localStorage.setItem(`schedule_start_time_${user.id}`, scheduleStartTime.toString());
            debugLog(`🚀 [SCHEDULE GENERATION STARTED] Schedule enhancement initiated - this will take 10-15 seconds`);
          }
          
          // Start enhancement process
          debugLog('🎯 [ENHANCEMENT] Calling /user/enhance-schedule endpoint...');
          const enhanceResponse = await fetch(`${API_URL}/user/enhance-schedule`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          });
          
          debugLog('🎯 [ENHANCEMENT] Enhancement API response status:', enhanceResponse.status);
          
          if (enhanceResponse.ok) {
            const enhanceData = await enhanceResponse.json();
            debugLog('✅ [ENHANCEMENT] Schedule enhancement started successfully:', enhanceData);
            
            // 🎯 FIX: Completion message will be logged via SSE events in ChatInterface
            // The enhancement completion will be detected when the SSE message arrives
            // and will log the completion message with timing
          } else {
            const errorText = await enhanceResponse.text();
            debugError('❌ [ENHANCEMENT] Failed to start schedule enhancement:', enhanceResponse.status, errorText);
          }
          
          return; // Exit early since we found existing schedules
        } else {
          debugLog('⚠️ [ENHANCEMENT] No existing schedules found in response');
        }
      } else {
        const errorText = await existingSchedulesResponse.text();
        debugError('❌ [ENHANCEMENT] Failed to fetch existing schedules:', existingSchedulesResponse.status, errorText);
      }
      
      // No existing schedules found, generate full schedule (fallback)
      debugLog('🔄 [ENHANCEMENT] No basic schedules found, generating full schedule as fallback');
      generateScheduleInBackground();
      
    } catch (error) {
      debugError('❌ [ENHANCEMENT] Error in checkAndStartEnhancement:', error);
      // Fallback to full generation
      generateScheduleInBackground();
    }
  };

  // Function to generate schedule in background (called after paywall)
  const generateScheduleInBackground = async () => {
    // 🎯 NEW: Prevent duplicate schedule generation
    const generationKey = `schedule_generation_${user?.id}`;
    if (localStorage.getItem(generationKey)) {
      debugLog('Schedule generation already in progress, skipping duplicate request');
      return;
    }
    
    try {
      // Mark generation as in progress
      localStorage.setItem(generationKey, 'true');
      
      // Start schedule generation but don't wait for it
      const API_URL = getApiUrl();
      const token = await getToken();
      
      // Check if schedules already exist to prevent duplicates
      try {
        const existingSchedulesResponse = await fetch(`${API_URL}/api/regimens/weekly`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (existingSchedulesResponse.ok) {
          const existingSchedules = await existingSchedulesResponse.json();
          if (existingSchedules && existingSchedules.length > 0) {
            debugLog('Schedules already exist, skipping generation');
            localStorage.removeItem(generationKey); // Clean up the flag
            return;
          }
        }
      } catch (error) {
        debugError('Error checking existing schedules:', error);
      }
      
      // First try to get onboarding data to generate personalized regimens
      let onboardingData = null;
      try {
        const onboardingResponse = await fetch(`${API_URL}/user/onboarding-data`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (onboardingResponse.ok) {
          const onboardingResult = await onboardingResponse.json();
          onboardingData = onboardingResult.onboarding_data;
          debugLog('Retrieved onboarding data for schedule generation:', onboardingData);
        }
      } catch (error) {
        debugError('Error fetching onboarding data:', error);
      }
      
      // Store start time for completion tracking
      const scheduleStartTime = Date.now();
      if (user) {
        localStorage.setItem(`schedule_start_time_${user.id}`, scheduleStartTime.toString());
        debugLog(`🚀 [SCHEDULE GENERATION STARTED] Schedule creation initiated - this will take 10-15 seconds`);
      }
      
      // Generate initial regimens with onboarding data
      const response = await fetch(`${API_URL}/user/generate-schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        debugLog('Initial regimen generation API response received:', data);
        
        // 🎯 FIX: Removed setTimeout-based refresh - refresh will be triggered by regimen_success event
        // from the backend when schedule generation actually completes
        
        // 🎯 FIX: Completion message will be logged via SSE events in ChatInterface
        // The 'runium:schedule-ready' event will be dispatched when the schedule is ready
        // and handleScheduleReady will log the completion message with timing
      } else {
        debugError('Failed to generate initial regimens');
        const errorData = await response.text();
        debugError('Error response:', errorData);
      }
    } catch (error) {
      debugError('Error starting schedule generation:', error);
    } finally {
      // 🎯 NEW: Always clean up the generation flag when done
      localStorage.removeItem(generationKey);
      debugLog('Schedule generation flag cleaned up');
    }
  };

  // Helper: common post-paywall transition logic
  const enterDashboard = () => {
    setShowPaywall(false);
    setActiveTab('chat');
    checkAndStartEnhancement();
    window.dispatchEvent(new Event('runium:refresh-chat'));
    setTimeout(() => {
      window.dispatchEvent(new Event('runium:refresh-schedule'));
    }, 2000);
  };

  // Onboarding screen
  if (effectiveShowLanding === true) {
    return <Onboarding onComplete={() => {
      debugLog('Dashboard: Onboarding completed');
      setUserOnboardingStatus(true);
      if (user) {
        localStorage.setItem(`schedule_generated_by_onboarding_${user.id}`, 'true');
      }
      setShowLanding(false);
      // Show paywall after onboarding if subscriptions are enabled and user isn't subscribed
      if (FEATURE_FLAGS.PAID_SUBSCRIPTIONS && !isSubscribed) {
        setShowPaywall(true);
      } else {
        enterDashboard();
      }
    }} />;
  }

  // Paywall screen
  if (FEATURE_FLAGS.PAID_SUBSCRIPTIONS && showPaywall && !isSubscribed) {
    return <Paywall
      onSubscribe={async () => {
        const result = await purchase();
        if (result.success) {
          enterDashboard();
        }
        return result;
      }}
      onRedeemPromo={async (code) => {
        const result = await redeemPromoCode(code);
        if (result.success) {
          setTimeout(() => enterDashboard(), 1000);
        }
        return result;
      }}
      onRestorePurchases={async () => {
        await restorePurchases();
        // useEffect above will auto-dismiss paywall if isSubscribed becomes true
      }}
      onBack={null}
      isLoading={subscriptionLoading}
    />;
  }

  return (
    <>
      {/* Loading overlay for delete account */}
      {FEATURE_FLAGS.DELETE_ACCOUNT && isDeleting && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900/80 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl p-8 shadow-xl border border-red-700/50 max-w-sm mx-4">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-red-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-white mb-2">Deleting Account</h3>
              <p className="text-gray-300 text-sm">Removing all data and deleting your account...</p>
              <p className="text-gray-400 text-xs mt-2">This action cannot be undone</p>
            </div>
          </div>
        </div>
      )}

      

      <div className="flex-1 min-h-0 flex flex-col bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800">
        <nav className="flex-shrink-0 bg-gray-800/90 text-white shadow-lg border-b border-gray-700/70 backdrop-filter backdrop-blur-sm relative z-40">
          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <div className="flex justify-between items-center h-14 sm:h-16">
                <div className="flex items-center min-w-0">
                  <RuniumIcon className="w-6 h-6 sm:w-7 sm:h-7 mr-2 text-blue-400 filter drop-shadow-[0_0_4px_rgba(59,130,246,0.3)] flex-shrink-0" />
                  <h1 className="text-lg sm:text-xl font-bold text-white truncate">Runium</h1>
                </div>
              
              {/* Navigation Tabs - Only Coach and Schedule */}
              <div className="flex items-center gap-1 bg-gray-700/50 rounded-lg p-1">
                <button
                  onClick={() => animateMainViewTo('chat')}
                  className={`flex items-center justify-center gap-1.5 px-3 min-h-[44px] rounded-md text-sm font-medium transition-all w-[85px] active:scale-95 ${
                    activeTab === 'chat'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-300 hover:text-white hover:bg-gray-600/50'
                  }`}
                >
                  <ChatIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Coach</span>
                </button>
                <button
                  onClick={() => {
                    // Stop the shimmer animation after first click
                    localStorage.setItem('schedule_button_clicked', 'true');
                    animateMainViewTo('schedule');
                  }}
                  className={`flex items-center justify-center gap-1.5 px-3 min-h-[44px] rounded-md text-sm font-medium transition-all min-w-[85px] relative overflow-hidden active:scale-95 ${
                    activeTab === 'schedule'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-300 hover:text-white hover:bg-gray-600/50'
                  }`}
                >
                  <CalendarIcon className="w-4 h-4 flex-shrink-0 relative z-10" />
                  <span className="hidden sm:inline relative z-10">Schedule</span>
                  {/* Shimmer animation for newly onboarded users */}
                  {!localStorage.getItem('schedule_button_clicked') && activeTab !== 'schedule' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent animate-shimmer"></div>
                  )}
                </button>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <UserButton 
                    fallback={<UserButtonSkeleton />}
                    signInUrl="/sign-in"
                    afterSwitchSessionUrl="/"
                    appearance={{
                      baseTheme: "dark",
                      variables: {
                        colorBackground: '#111827',
                        colorText: '#f9fafb',
                        colorTextSecondary: '#9ca3af',
                        colorPrimary: '#3b82f6',
                        colorDanger: '#ef4444',
                        colorSuccess: '#10b981',
                        colorWarning: '#f59e0b',
                        borderRadius: '0.75rem',
                        fontFamily: 'inherit',
                        fontSize: '0.875rem'
                      },
                      elements: {
                        userButtonPopoverCard: {
                          pointerEvents: "auto"
                        },
                        userButtonPopoverActions: {
                          gap: "0.5rem"
                        }
                      }
                    }}
                  >
                    <UserButton.MenuItems>
                      <UserButton.Action 
                        label="Prompt Settings"
                        labelIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" clipRule="evenodd" />
                          </svg>
                        }
                        onClick={() => setActiveTab('prompt-settings')}
                      />
                      <UserButton.Action 
                        label="Download Weekly PDF"
                        labelIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" clipRule="evenodd" />
                          </svg>
                        }
                        onClick={() => {
                          setActiveTab('schedule');
                          // Generate PDF directly
                          setTimeout(() => {
                            handleGeneratePDF();
                          }, 100);
                        }}
                      />
                      <UserButton.Action 
                        label="Notification Preferences"
                        labelIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M5.25 9a6.75 6.75 0 0113.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 01-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 11-7.48 0 24.585 24.585 0 01-4.831-1.243.75.75 0 01-.298-1.205A8.217 8.217 0 005.25 9.75V9z" clipRule="evenodd" />
                          </svg>
                        }
                        onClick={() => setActiveTab('notification-settings')}
                      />
                      {FEATURE_FLAGS.DELETE_ACCOUNT && (
                        <UserButton.Action 
                          label={isDeleting ? "Deleting..." : "Delete Account"}
                          labelIcon={
                            isDeleting ? (
                              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-red-400">
                                <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48 0a.75.75 0 10-1.498.058l.347 9a.75.75 0 001.5-.058l-.345-9z" clipRule="evenodd" />
                              </svg>
                            )
                          }
                          onClick={handleDeleteAccount}
                        />
                      )}
                      {FEEDBACK_FEATURE_ENABLED && (
                        <UserButton.Action
                          data-feedback-badge="new"
                          label="Send Feedback"
                          labelIcon={
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                              <path d="M4 5c0-1.105.895-2 2-2h12a2 2 0 012 2v10.382a2 2 0 01-.586 1.414l-4.618 4.618A2 2 0 0113.382 22H6a2 2 0 01-2-2V5zm3.5 3a.75.75 0 000 1.5h9a.75.75 0 000-1.5h-9zm0 3a.75.75 0 000 1.5h5a.75.75 0 000-1.5h-5z" />
                            </svg>
                          }
                          onClick={() => setShowFeedbackForm(true)}
                        />
                      )}
                      {isAdminUser && (
                        <UserButton.Action
                          label="Admin Feedback Console"
                          labelIcon={
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                              <path d="M4.5 3.75A.75.75 0 015.25 3h13.5a.75.75 0 01.75.75V6a.75.75 0 01-.75.75H5.25A.75.75 0 014.5 6V3.75zM4.5 9a.75.75 0 01.75-.75h5.5a.75.75 0 01.75.75v11.25a.75.75 0 01-.75.75h-5.5a.75.75 0 01-.75-.75V9zm8.25 0a.75.75 0 01.75-.75h5.5a.75.75 0 01.75.75v6.75a.75.75 0 01-.75.75h-5.5a.75.75 0 01-.75-.75V9z" />
                            </svg>
                          }
                          onClick={() => navigate('/admin/feedback')}
                        />
                      )}
                      {isAdminUser && (
                        <UserButton.Action
                          label="Training Rules Admin"
                          labelIcon={
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                              <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 5.12a.75.75 0 01-.542.542l-1.303.178a1.875 1.875 0 00-1.567 1.85v1.587c0 .918.663 1.7 1.567 1.85l1.303.179a.75.75 0 01.542.541l.178 1.303c.151.904.933 1.567 1.85 1.567h1.844c.917 0 1.699-.663 1.85-1.567l.178-1.303a.75.75 0 01.541-.541l1.303-.179a1.875 1.875 0 001.567-1.85V9.257c0-.917-.663-1.699-1.567-1.85l-1.303-.178a.75.75 0 01-.541-.542l-.179-1.303a1.875 1.875 0 00-1.85-1.567h-1.844zM12 10.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" clipRule="evenodd" />
                              <path d="M10.5 15.75a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75zM10.5 18.75a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75z" />
                            </svg>
                          }
                          onClick={() => navigate('/admin/training-rules')}
                        />
                      )}
                      <UserButton.Action 
                        label="About Runium"
                        labelIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.906l-.709 2.837c-.194.897.105 1.319.808 1.319s1.003-.422 1.808-1.319l-.707-2.837c-.312-1.443.98-2.479 2.126-1.906l1.83.872c1.041.495 1.83 1.76 1.83 3.01 0 1.885-1.53 3.413-3.413 3.413-1.885 0-3.413-1.528-3.413-3.413 0-1.25.789-2.515 1.83-3.01l1.83-.872zM12 10.5a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                          </svg>
                        }
                        onClick={() => setShowAboutModal(true)}
                      />
                    </UserButton.MenuItems>
                  </UserButton>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="bg-gray-800/90 text-white py-3 px-4 sm:hidden animate-fade-in border-b border-gray-700/70 backdrop-filter backdrop-blur-sm">
            <div className="border-t border-gray-700/70 pt-3">
              <div className="text-sm text-gray-300">
                <p className="mb-1">Your AI Running Coach</p>
                <p>Ask any running related questions!</p>
              </div>
            </div>
          </div>
        )}

        <main className="max-w-7xl mx-auto bg-gray-900/90 backdrop-filter backdrop-blur-sm sm:rounded-b-xl sm:shadow-xl overflow-hidden sm:border-x sm:border-b border-gray-700/70 flex-1 min-h-0" style={{
          display: 'grid',
          gridTemplateRows: (activeTab === 'notification-settings' || activeTab === 'prompt-settings') ? 'auto 1fr' : '1fr',
        }}>
          {/* Back button for settings pages */}
          {(activeTab === 'notification-settings' || activeTab === 'prompt-settings') && (
            <div style={{ gridRow: '1', overflow: 'hidden' }} className="bg-gray-800 px-4 py-3 border-b border-gray-700">
              <button
                onClick={() => setActiveTab('chat')}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Coach
              </button>
            </div>
          )}
          
          <div style={{ gridRow: (activeTab === 'notification-settings' || activeTab === 'prompt-settings') ? '2' : '1', overflow: 'hidden', minHeight: 0 }}>
            {renderActiveTab()}
          </div>
        </main>
        
        {/* About Modal */}
        {showAboutModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAboutModal(false)}
          >
            <div 
              className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 max-w-md w-full mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">About Runium</h2>
                <button
                  onClick={() => setShowAboutModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Backend Version</h3>
                  <VersionDisplay detailed className="text-gray-300" />
                </div>
                
                <div className="pt-4 border-t border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-2">Beta Testing</h3>
                  <p className="text-gray-300 text-sm">
                    You're using a beta version of Runium. Thank you for testing! Your feedback helps us improve the app.
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    If you encounter any issues or have suggestions, please report them through the chat interface or contact the development team.
                  </p>
                </div>
                
                {FEATURE_FLAGS.LEGAL_LINKS && (
                  <div className="pt-4 border-t border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-3">Legal</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          openLegalUrl('https://runium.ai/privacy');
                          setShowAboutModal(false);
                        }}
                        className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-gray-200 group-hover:text-white">Privacy Policy</span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          openLegalUrl('https://runium.ai/terms');
                          setShowAboutModal(false);
                        }}
                        className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-gray-200 group-hover:text-white">Terms of Service</span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-700">
                <button
                  onClick={() => setShowAboutModal(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {FEEDBACK_FEATURE_ENABLED && (
          <FeedbackForm
            isOpen={showFeedbackForm}
            onClose={() => setShowFeedbackForm(false)}
            defaultType={feedbackDefaults.type}
            defaultTitle={feedbackDefaults.title}
            contextSource={feedbackDefaults.context}
        recentActions={feedbackRecentActions}
        activeConversationId={activeConversationId}
        onboardingComplete={!!userOnboardingStatus}
        experimentAssignments={experimentAssignments}
          />
        )}
        {/* MessageInput is now rendered inside ChatInterface as part of flex layout */}
      </div>
    </>
  );
};

export default Dashboard; 
